/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Close } from '@material-ui/icons';
import {
  TemplateIcon,
  AttachFileIcon,
  SMSIcon,
  WhatsappIcon,
} from '~/assets/svgIcons';
import * as lib from '~/dependencies';
import { colors } from '~/layout';
import { ChannelTypeEnum } from '~/enums/conversations';
import { DraftMessage } from '~/interfaces/conversations';
import { SmsTemplate } from '~/types/accountInfo';
import { checkGSMAlphabet, countLineBreaks, eventTracker } from '~/helpers';
import Templates from '../Templates';
import TemplateParamsEditor from '../TemplateParamsDialog';
import FilePickerPreview from '../FilePickerPreview';
import SendChannelButton from '../SendChannelButton';
import {
  Bold,
  Button,
  ClearTemplateButton,
  Container,
  Input,
  Row,
  TemplateBanner,
  TemplateBannerLabel,
  WarnMessage,
  Actions,
} from './styles';

const MAX_SMS_MSG_LNG = 960;
const MAX_WHATSAPP_MSG_LNG = 1600;

/**
 * Inserts text at the current cursor location of a textarea or input element.
 * @param inputRef Reference to the input or textarea element
 * @param text Text to be inserted
 */
// function insertTextAtCursorLocation(inputRef: any, text: string) {
//   const start = inputRef.selectionStart;
//   const end = inputRef.selectionEnd;

//   inputRef.value =
//     inputRef.value.slice(0, start) + text + inputRef.value.slice(end);

//   // Move cursor after inserted text
//   const newPosition = start + text.length;
//   inputRef.selectionStart = newPosition;
//   inputRef.selectionEnd = newPosition;
// }

type MessageTextInputProps = {
  onSend: (draftMessage: DraftMessage) => void;
  /**
  }) => void;
  /**
   * Emitted when the user add a template with usage '10dlc'
   * @param template returns the selected template
   */
  onTemplateAppend?: (template: SmsTemplate) => void;
  onTemplateRemoved?: () => void;
  channelType: ChannelTypeEnum;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  placeholderFontSize?: string;
  indicateTemplateRequired?: boolean;
  hasNoReceipientsOrSender?: boolean;
  isExpired?: boolean;
  /**
   * Optional list of channels to show in the channel selector dropdown.
   */
  channelOptions: ChannelTypeEnum[];
  onChannelChange: (channel: ChannelTypeEnum) => void;
};

// Matches anything between {{}}
const TEMPLATE_PARAMS_REGEX = /\{\{.+?\}\}/;

function addTemplateHighlight(text: string) {
  return text.replace(/(\{\{.+?\}\})/g, '<span class="highlight">$1</span>'); // /g needed here to replace all occurrences
}

function cleanTemplateHighlight(element: HTMLDivElement | null) {
  return (
    element?.innerHTML
      ?.replace(/<span class="highlight">/g, '')
      ?.replace(/<\/span>/g, '')
      ?.replace(/<br>/g, '') ?? ''
  );
}

/**
 * Checks whether tempalte message contains parameters based on the TEMPLATE_PARAMS_REGEX pattern.
 */
function hasTemplateVariables(templateMessage: string = ''): boolean {
  return TEMPLATE_PARAMS_REGEX.test(templateMessage);
}

function mapChannelOptions(types: ChannelTypeEnum[]) {
  return types
    .map(type => {
      const channelOptionMap: Partial<Record<
        ChannelTypeEnum,
        { type: ChannelTypeEnum; label: string; icon: string; color: string }
      >> = {
        [ChannelTypeEnum.WHATSAPP]: {
          type: ChannelTypeEnum.WHATSAPP,
          label: 'WhatsApp',
          icon: WhatsappIcon,
          color: colors.green08,
        },
        [ChannelTypeEnum.SMS]: {
          type: ChannelTypeEnum.SMS,
          label: 'SMS',
          icon: SMSIcon,
          color: colors.lightBlue12,
        },
      };
      return channelOptionMap[type];
    })
    .filter((opt): opt is NonNullable<typeof opt> => Boolean(opt));
}

export default function TextInput({
  onSend,
  onTemplateAppend,
  onTemplateRemoved,
  channelType,
  disabled,
  isLoading,
  placeholder,
  placeholderFontSize,
  indicateTemplateRequired,
  hasNoReceipientsOrSender,
  isExpired,
  channelOptions,
  onChannelChange,
}: MessageTextInputProps) {
  // REF's
  const textareaRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef(null);

  // HOOK's
  const { t } = lib.useTranslation();

  // STATE's
  const [maxTextLng, setMaxTextLng] = useState<boolean>(false);
  const [invalidChars, setInvalidChars] = useState<string[]>([]);
  const [message, setMessage] = useState<string>('');
  const [template, setTemplate] = useState<SmsTemplate | null>(null);
  const [templateParams, setTemplateParams] = useState(undefined);
  const [file, setFile] = useState<File>();
  const [files, setFiles] = useState<File[]>([]);
  const [openTemplateList, setOpenTemplateList] = useState<boolean>(false);
  const [openTemplateParamsEditor, setOpenTemplateParamsEditor] = useState<
    boolean
  >(false);

  // CONST's
  const hasError = maxTextLng || invalidChars.length > 0;
  const hasInvalidChars = invalidChars.length > 0;
  const invalidCharsText = invalidChars.join(', ');
  const hasMessageContent = message.trim().length > 0;
  const hasSelectedFile = Boolean(file);
  const isWhatsAppChannel = channelType === ChannelTypeEnum.WHATSAPP;
  const isWhatsAppTemplate = template?.usage === 'whatsapp';
  const isDisabled = isWhatsAppChannel
    ? disabled || hasError || (!file && message?.length < 1)
    : disabled || hasError || message?.length < 1;

  const isPlaceholderLocked = Boolean(placeholder);
  const hasTemplateParams = hasTemplateVariables(template?.messageText);
  const isTemplateRequired =
    isWhatsAppChannel && !template && indicateTemplateRequired;

  const isInputDisabled =
    Boolean(disabled) || isWhatsAppTemplate || hasSelectedFile;
  const isSendButtonDisabled =
    isLoading ||
    isDisabled ||
    (template && hasTemplateParams && !templateParams);

  const MAX_MSG_LNG = isWhatsAppChannel
    ? MAX_WHATSAPP_MSG_LNG
    : MAX_SMS_MSG_LNG;

  const validateSMSMessage = (msg: string) => {
    const invalidCharsList = checkGSMAlphabet(msg);

    if (invalidCharsList.length > 0) {
      setInvalidChars([...invalidCharsList]);
    } else {
      setInvalidChars([]);
    }

    setMaxTextLng(msg?.length > MAX_MSG_LNG);

    setMessage(msg);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validateWhatsAppMessage = (msg: string) => {
    const invalidCharsList = checkGSMAlphabet(msg);

    if (invalidCharsList.length > 0) {
      setInvalidChars([...invalidCharsList]);
    } else {
      setInvalidChars([]);
    }

    setMaxTextLng(msg?.length > MAX_MSG_LNG);

    setMessage(msg);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validateInternalMessage = (msg: string) => {
    // TODO:
  };

  const validatorFactory = (type: ChannelTypeEnum) => {
    switch (type) {
      case ChannelTypeEnum.SMS:
        return { validate: (msg: string) => validateSMSMessage(msg) };
      case ChannelTypeEnum.WHATSAPP:
        return { validate: (msg: string) => validateWhatsAppMessage(msg) };
      case ChannelTypeEnum.INTERNAL:
        return { validate: (msg: string) => validateInternalMessage(msg) };
      default:
        return { validate: (msg: string) => validateSMSMessage(msg) };
    }
  };

  const validator = validatorFactory(channelType);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    if (disabled) return;

    const textarea = textareaRef.current;
    const plainText = cleanTemplateHighlight(e.currentTarget);

    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;

    validator.validate(plainText);
  };

  const handleOnTemplateChange = (activeTemplate: SmsTemplate) => {
    const textarea = textareaRef.current;

    if (textarea) {
      const { messageText: templateMessage } = activeTemplate;

      if (onTemplateAppend) {
        onTemplateAppend(activeTemplate);
      }

      // Line height based on current font size font-size: 16px;
      const fontLineHeight = 22.5;
      const updatedMessage = isWhatsAppChannel
        ? templateMessage
        : `${message} ${templateMessage}`;
      const lineCount = countLineBreaks(updatedMessage);

      // @ts-ignore
      textarea.style.height = 'auto';
      // @ts-ignore
      textarea.style.height = `${lineCount * fontLineHeight}px`;

      // insertTextAtCursorLocation(textarea, templateMessage);

      // This update will render immediately
      flushSync(() => {
        setOpenTemplateList(false);
        setTemplate(activeTemplate);
        setMaxTextLng(updatedMessage.length > MAX_MSG_LNG);
        setMessage(updatedMessage);
        textarea.innerHTML = addTemplateHighlight(updatedMessage);

        // @ts-ignore
        // setTimeout(() => textarea.focus(), 300);
      });
    }
  };

  const afterSentCleanup = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.textContent = '';
    textarea.style.height = 'auto';
    setMessage('');
    setMaxTextLng(false);
    setInvalidChars([]);
    setTemplate(null);
    setTemplateParams(undefined);
    setFile(undefined);
    setFiles([]);
  }, []);

  useEffect(() => {
    afterSentCleanup();
  }, [channelType, afterSentCleanup]);

  // Clear the input if message is just a <br> tag, which can happen when user deletes all text in a contentEditable div
  useEffect(() => {
    if (message === '<br>') {
      setMessage('');
    }
  }, [message, setMessage]);

  function sendNewMessage() {
    // @ts-ignore
    const { textContent } = textareaRef.current;

    if (onSend && (textContent.length > 0 || template?.id || file)) {
      const payload = {
        channelType,
        text: String(textContent),
        ...(isWhatsAppChannel && {
          templateId: template?.id,
          templateParams,
          ...(file && { file }),
        }),
      };
      onSend(payload);

      afterSentCleanup();
      // @ts-ignore
      eventTracker(events => events.SendSMSClicked);
    }
  }

  const handleClearTemplate = () => {
    afterSentCleanup();

    if (onTemplateRemoved) onTemplateRemoved();
  };

  const handleSendButtonClick = () => {
    if (disabled) return;

    const textarea = textareaRef.current;
    if (!textarea) return;

    if (isWhatsAppTemplate && hasTemplateParams && !templateParams) {
      setOpenTemplateParamsEditor(true);
    } else {
      // @ts-ignore
      sendNewMessage();
    }
  };

  /**
   * WhatsApp template: If Whatsapp template is selected, input can be clicked regardless of disabled
   */
  const handleInputMessageClick = () => {
    if (disabled && !isWhatsAppChannel) return;
    if (!hasTemplateParams) return;
    setOpenTemplateParamsEditor(true);
  };

  /**
   * Allow opening templates if:
   * It's a WhatsApp channel (enabled even when disabled), OR
   * The input is not disabled
   */
  const toggleTemplateList = () => {
    if (hasSelectedFile) return;

    if (!hasNoReceipientsOrSender) {
      setOpenTemplateList(!openTemplateList);
    }
  };

  const handleOpenFilePicker = () => {
    if (disabled) return;
    if (isExpired) return;
    if (hasMessageContent) return;

    // @ts-ignore
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const renderInputMessage = (
    <Input
      // @ts-ignore
      onPaste={event => {
        if (event?.clipboardData?.getData('text')) return;
        event.preventDefault();
        lib.ToastMessage(
          'warn',
          'Paste file not allowed. Please use the attachment button to send files.'
        );
      }}
      id="input-message"
      data-test-id="input-message"
      rows={1}
      ref={textareaRef}
      style={
        {
          '--placeholder-font-size': placeholderFontSize ?? '16px',
        } as React.CSSProperties
      }
      {...(!hasSelectedFile && {
        'data-placeholder':
          placeholder ?? t('screens.conversation.txtInputText'),
      })}
      onInput={handleInput}
      aria-disabled={isPlaceholderLocked || Boolean(disabled)}
      contentEditable={isPlaceholderLocked ? false : !isInputDisabled}
      {...(isWhatsAppTemplate && {
        isSpecialTemplate: true,
        onClick: handleInputMessageClick,
      })}
    />
  );

  return (
    <Container hasError={hasError} className="message-text-input-container">
      {hasError ? (
        <WarnMessage borderBottom={hasError}>
          {maxTextLng && (
            <span>
              {t('screens.conversation.smsMaxCharLng')}{' '}
              <Bold>
                {message.length}/{MAX_MSG_LNG}
              </Bold>
              .
            </span>
          )}
          {hasInvalidChars && (
            <span>
              {t('screens.conversation.smsInvalidChars')}{' '}
              <Bold>{invalidCharsText}</Bold>
            </span>
          )}
        </WarnMessage>
      ) : null}

      {openTemplateParamsEditor && (
        <TemplateParamsEditor
          template={template!}
          onClose={() => setOpenTemplateParamsEditor(!openTemplateParamsEditor)}
          onParamsFilled={(paramValues: any, processedMessage: string) => {
            setTemplateParams(paramValues);
            // @ts-ignore
            textareaRef.current.textContent = processedMessage;
          }}
        />
      )}

      {template && (
        <TemplateBanner>
          <TemplateBannerLabel>
            {t('screens.conversation.template')}:{' '}
            <b title={template.name}>{template.name}</b>
          </TemplateBannerLabel>
          <ClearTemplateButton
            type="button"
            data-test-id="clear-template"
            title={t('screens.conversation.clearTemplate')}
            onClick={handleClearTemplate}>
            <Close fontSize="small" />
          </ClearTemplateButton>
        </TemplateBanner>
      )}

      <Row>
        <Actions>
          {isWhatsAppChannel && !hasMessageContent && (
            <Button
              title="Files"
              aria-label="Open file picker"
              data-test-id="media-button"
              src={AttachFileIcon}
              onClick={handleOpenFilePicker}
              active={Boolean(file)}
            />
          )}

          <Button
            title="Templates"
            aria-label="Open template selector"
            data-test-id="template-selector"
            src={TemplateIcon}
            onClick={toggleTemplateList}
            highlight={isTemplateRequired}
            active={Boolean(template)}
          />
        </Actions>

        <div className="input-wrapper">
          {isWhatsAppChannel ? (
            <FilePickerPreview
              files={files}
              onFileChange={(acceptedFile: File | undefined) => {
                if (hasMessageContent && acceptedFile) return;
                setFile(acceptedFile);
              }}
              onPreviewChange={(filePreviewList: File[]) => {
                if (hasMessageContent && filePreviewList.length > 0) return;
                setFiles(filePreviewList);
              }}
              fileInputRef={fileInputRef}>
              {renderInputMessage}
            </FilePickerPreview>
          ) : (
            renderInputMessage
          )}
        </div>

        <SendChannelButton
          data-test-id="send-message"
          onSend={handleSendButtonClick}
          disabled={Boolean(isSendButtonDisabled)}
          isLoading={isLoading}
          onChannelChange={onChannelChange}
          channelOptions={mapChannelOptions(channelOptions)}
        />
      </Row>
      {openTemplateList && (
        <Templates
          channelType={channelType}
          onClose={toggleTemplateList}
          onTemplateChange={handleOnTemplateChange}
        />
      )}
    </Container>
  );
}
